import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Rating,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { getClient } from "../API/clientAPI";
import { getJobOrder, deleteJobOrder } from "../API/jobOrderAPI"; // Add deleteJobOrder import

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AreasTable from "./FormComponents/AreasTable";
import FindingsCard from "./FormComponents/FindingsCard";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function DeleteQcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState(""); // Add this state

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, []); // Dependency array ensures the effect runs when ID changes

  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  // view-only nature of work
  const nature = jobOrder?.nature_of_work
    ?? {};

  // View-only source for TYPE OF STRUCTURE
  const st = jobOrder?.qc_structure_type ?? {};

  // View-only source for GPC
  const gpcData = jobOrder?.gpc ?? {};
  const insectsDetail = gpcData.insects_detail ?? {};
  const insectsOn = Boolean(gpcData.flying_and_crawling_insects);

  // View-only source for Action Taken / Treatment Done
  const act = jobOrder?.action_taken_treatment_done ?? {};

  // --- AREAS TREATED: helpers & constants ---
  const makeIdxKey = (rIdx, cIdx) => `r${rIdx}-c${cIdx}`;

  const AREAS_TREATED_LAYOUT = [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ];

  // find the coordinates of a label (case-insensitive)
  const findLabelKey = (layout, search) => {
    const target = String(search).toLowerCase();
    for (let r = 0; r < layout.length; r++) {
      for (let c = 0; c < layout[r].length; c++) {
        const lbl = layout[r][c];
        if (lbl && String(lbl).toLowerCase() === target) {
          return makeIdxKey(r, c);
        }
      }
    }
    return null;
  };

  // map selections (1-based in DB) -> state keys, but ONLY for cells that have a non-null label
  const areasPos = jobOrder?.areas_treated_positions ?? [];
  const areasTreatedViewState = React.useMemo(() => {
    const s = {};
    for (const { row, col } of areasPos) {
      const rIdx = (Number(row) || 1) - 1;
      const cIdx = (Number(col) || 1) - 1;
      if (
        rIdx >= 0 &&
        cIdx >= 0 &&
        AREAS_TREATED_LAYOUT[rIdx] &&
        AREAS_TREATED_LAYOUT[rIdx][cIdx] // ensure label exists
      ) {
        s[makeIdxKey(rIdx, cIdx)] = true;
      }
    }
    return s;
  }, [areasPos]);

  // toggle 'Other areas' text enablement only if "Other areas" cell is checked
  const OTHER_AREAS_KEY = React.useMemo(
    () => findLabelKey(AREAS_TREATED_LAYOUT, "Other areas"),
    []
  );

  const otherAreasTextView = jobOrder?.areas_treated_other_text ?? "";

  // --- FINDINGS (view-only) ---
  const degree = jobOrder?.findings_degree_of_infestation ?? {};
  const DEGREE_ROWS = [
    { key: 'flies',      label: 'Flies' },
    { key: 'mosquitoes', label: 'Mosquitoes' },
    { key: 'roaches',    label: 'Roaches' },
    { key: 'ants',       label: 'Ants' },
    { key: 'rodents',    label: 'Rodents' },
  ];
  const DEGREE_LEVELS = ['L', 'M', 'H'];

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'common.white' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  // RHF — single form instance for the page
  const { control, register, setValue } = useForm({
    defaultValues: {
      findingsAreas: [],
      recommendations: [],
    },
  });

  // ---- FINDINGS AREAS ----
  const {
    fields: findingsAreas,
    replace: replaceFindingAreas,
  } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  useEffect(() => {
    if (!jobOrder?.findings_areas) return;

    const mapped = jobOrder.findings_areas.map((a) => ({
      location: a?.location ?? "",
      pests: {
        Roaches: Number(a?.pests?.Roaches ?? 0),
        Mosq:    Number(a?.pests?.Mosq    ?? 0),
        Flies:   Number(a?.pests?.Flies   ?? 0),
        Ants:    Number(a?.pests?.Ants    ?? 0),
        Rodents: Number(a?.pests?.Rodents ?? 0),
        Others:  Number(a?.pests?.Others  ?? 0),
      },
    }));

    replaceFindingAreas(mapped);
  }, [jobOrder?.findings_areas, replaceFindingAreas]);

  // ---- RECOMMENDATIONS ----
  const MAX_RECOMMENDATIONS = 8;

  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  const {
    fields: recFields,
    replace: replaceRec,
  } = useFieldArray({
    control,
    name: "recommendations",
  });

  useEffect(() => {
    if (!jobOrder?.recommendations) return;

    const mapped = jobOrder.recommendations.map((r) => ({
      option: r?.option ?? "",
      other_text: r?.other_text ?? "",
    }));

    replaceRec(mapped);
  }, [jobOrder?.recommendations, replaceRec]);

  // View-only source for Customer's Responsibilities
  const cr = jobOrder?.customers_responsibilities ?? {};
  const sanitationList = Array.isArray(cr.sanitation) ? cr.sanitation : [];
  const san1 = sanitationList[0] ?? "";
  const san2 = sanitationList[1] ?? "";
  const san3 = sanitationList[2] ?? "";

  const sealing  = cr.rodent_entry_point?.sealing  ?? "";
  const barriers = cr.rodent_entry_point?.barriers ?? "";

  const repairsMaintenance = cr.repairs_maintenance ?? "";
  const renovation         = cr.renovation ?? "";

  // View-only source for Chemicals Used
  const chemicalsUsedView = jobOrder?.chemical_used ?? [];

  // View-only source for Control Against
  const ca = jobOrder?.control_against ?? {};

  const handleDelete = async () => {
    // Check if user typed "DELETE" correctly
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      setErrorMessage("Please type 'DELETE' to confirm deletion.");
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");
      
      // Call the delete API
      await deleteJobOrder(id);
      
      // Show success message or navigate back
      console.log("Service report deleted successfully");
      navigate("/manage-service-reports");
      
    } catch (err) {
      console.error("Error deleting service report:", err);
      setErrorMessage("Failed to delete service report. Please try again.");
    } finally {
      setIsLoading(false);
      setConfirmDialogOpen(false);
      setDeleteConfirmText(""); // Reset confirmation text
    }
  };

  const handleDeleteClick = () => {
    setConfirmDialogOpen(true);
    setDeleteConfirmText(""); // Reset confirmation text when opening dialog
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setDeleteConfirmText(""); // Reset confirmation text when canceling
  };

  // Check if delete button should be enabled
  const isDeleteButtonEnabled = deleteConfirmText.trim().toUpperCase() === "DELETE" && !isLoading;


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Delete Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        {/* Basic Information Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={2}>
                  <TextField
                    name="id"
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={2}>
                  <FormControl fullWidth>
                    <InputLabel id="job-order-status-label">
                      Job Order Status
                    </InputLabel>
                    <Select
                      labelId="job-order-status-label"
                      name="status"
                      value={jobOrder?.status || ""}
                      label="Job Order Status"
                      disabled
                    >
                      <MenuItem value="Not Done">Not Done</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Cancelled By Client">
                        Cancelled By Client
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client ID"
                    label="Client ID"
                    value={jobOrder?.client_id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client Name"
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={10}>
                      <TextField
                        label="Job Site Address"
                        variant="outlined"
                        fullWidth
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder.client.address.house_unit_street || ""
                            }, ${jobOrder.client.address.barangay || ""
                            }, ${jobOrder.client.address.city || ""
                            }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                            : ""
                        }
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                      <TextField
                        name="area"
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact
                        ? `${jobOrder.client.contact.lastname || ""}, ${jobOrder.client.contact.firstname || ""
                        }`
                        : ""
                    } // Display the contact's full name or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    variant="outlined"
                    value={jobOrder.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact?.number
                        ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                        : ""
                    } // Display the contact number or an empty string if not available
                    disabled
                  />
                </Grid>                
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    label="Location Type"
                    value={
                      client?.location_type
                        ? client.location_type
                        : ""
                    }
                    fullWidth
                    variant="outlined"
                    sx={{ fontSize: "0.875rem" }}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{                    
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={jobOrder.special_notes || ""}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrder?.job_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrder?.start_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrder?.end_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrder?.end_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Appointment
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="appointment-type-label">
                      Appointment Type
                    </InputLabel>
                    <Select
                      labelId="appointment-type-label"
                      name="appointment_type"
                      value={jobOrder?.appointment_type || ""}
                      label="appointment_type"
                      disabled
                    >
                      <MenuItem value="Initial">Initial</MenuItem>
                      <MenuItem value="Follow-up">Follow-up</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="treatment-type-label">
                      Treatment Type
                    </InputLabel>
                    <Select
                      labelId="treatment-type-label"
                      name="treatment_type"
                      value={jobOrder?.treatment_type || ""}
                      label="treatment_type"
                      disabled
                    >
                      <MenuItem value="QB">QB</MenuItem>
                      <MenuItem value="FFUP">FFUP</MenuItem>
                      <MenuItem value="LT">LT</MenuItem>
                      <MenuItem value="QBLT">QBLT</MenuItem>
                      <MenuItem value="Backjob">Backjob</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Nature of Work Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  NATURE OF WORK
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(nature.general_pest_control)}
                      disabled
                    />
                  }
                  label="General Pest Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(nature.disinfection)}
                      disabled
                    />
                  }
                  label="Disinfection"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(nature.fumigation)}
                      disabled
                    />
                  }
                  label="Fumigation"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(nature.rodent_control)}
                      disabled
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(nature.fogging_misting)}
                      disabled
                    />
                  }
                  label="Fogging/Misting"
                />

                {/* Others with text field beside it */}
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(nature.others)}
                        disabled
                      />
                    }
                    label="Others"
                  />
                  <TextField
                    label="Please specify"
                    value={nature.others_text ?? ""}
                    disabled
                    size="small"
                    sx={{ maxWidth: 320 }}
                  />
                </Box>
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Structure Type Section */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                TYPE OF STRUCTURE
              </Typography>
            }
            sx={{ pb: 0, px: 2, pt: 2 }}
          />
          <CardContent sx={{ px: 2, pb: 2 }}>
            <FormGroup sx={{ width: "100%" }}>
              {/* Residential */}
              <FormControlLabel
                sx={{ width: "100%" }}
                control={<Checkbox checked={Boolean(st.residential)} disabled />}
                label="Residential"
              />

              {/* Commercial Establishment + Nature of Business text field */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <FormControlLabel
                  control={<Checkbox checked={Boolean(st.commercial_establishment)} disabled />}
                  label="Commercial Establishment"
                />
                <TextField
                  label="Nature of Business"
                  size="small"
                  sx={{ maxWidth: 320 }}
                  value={st.nature_of_business ?? ""}
                  disabled
                />
              </Box>

              {/* Others + specify text field */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <FormControlLabel
                  control={<Checkbox checked={Boolean(st.others)} disabled />}
                  label="Others"
                />
                <TextField
                  label="Please specify"
                  size="small"
                  sx={{ maxWidth: 320 }}
                  value={st.others_text ?? ""}
                  disabled
                />
              </Box>
            </FormGroup>
          </CardContent>
        </Card>

        {/* For General Pest Control (GPC) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For General Pest Control (GPC)
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup>

                {/* Main: Flying & crawling insects */}
                <FormControlLabel
                  control={<Checkbox checked={insectsOn} disabled />}
                  label="Flying and crawling insects"
                />

                {/* Sub-options (rendered; view-only) */}
                <Box
                  sx={{
                    ml: 4,
                    mt: 0.5,
                    pl: 1,
                    borderLeft: insectsOn ? '2px solid' : '2px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <FormControlLabel
                      control={<Checkbox size="small" checked={Boolean(insectsDetail.cockroach)} disabled />}
                      label="Cockroach"
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={Boolean(insectsDetail.flies)} disabled />}
                      label="Flies"
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={Boolean(insectsDetail.mosquitoes)} disabled />}
                      label="Mosquitoes"
                    />
                  </Stack>
                </Box>

                {/* Main: Rodents */}
                <FormControlLabel
                  control={<Checkbox checked={Boolean(gpcData.rodents)} disabled />}
                  label="Rodents"
                />

                {/* Main: Other pests + text field beside it */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Checkbox checked={Boolean(gpcData.other_pests)} disabled />}
                    label="Other pests"
                  />
                  <TextField
                    label="Please specify"
                    size="small"
                    disabled
                    value={gpcData.other_pests_text ?? ''}
                    sx={{ flex: '1 1 260px', maxWidth: 360 }}
                  />
                </Box>

              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* ACTION TAKEN / TREATMENT DONE */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  ACTION TAKEN / TREATMENT DONE
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup>

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.mist_spraying)} disabled />}
                  label="Mist spraying"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.thermal_fogging)} disabled />}
                  label="Thermal fogging"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.cold_fogging)} disabled />}
                  label="Cold fogging"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.larviciding)} disabled />}
                  label="Larviciding"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.gel_baiting)} disabled />}
                  label="Gel baiting"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.uv_light)} disabled />}
                  label="UV light"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.wiping)} disabled />}
                  label="Wiping"
                />

                <FormControlLabel
                  control={<Checkbox checked={Boolean(act.steaming)} disabled />}
                  label="Steaming"
                />

                {/* Others + text field beside it */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Checkbox checked={Boolean(act.others)} disabled />}
                    label="Others:"
                  />
                  <TextField
                    label="Please specify"
                    size="small"
                    value={act.others_text ?? ''}
                    disabled
                    sx={{ flex: '1 1 260px', maxWidth: 360 }}
                  />
                </Box>

              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* AREAS TREATED Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  AREAS TREATED
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AreasTable
                  layout={AREAS_TREATED_LAYOUT}
                  state={areasTreatedViewState}
                  onToggle={() => () => { /* view-only */ }}
                  ariaLabel="areas_treated"
                  getKey={(rIdx, cIdx /*, label */) => makeIdxKey(rIdx, cIdx)}
                />

                <TextField
                  label="Please specify (Other areas)"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={otherAreasTextView}
                  disabled={!OTHER_AREAS_KEY || !areasTreatedViewState[OTHER_AREAS_KEY]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* FINDINGS */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  FINDINGS
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {/* Sub-header */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Degree of Infestation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                L - Light, M - Medium, H - High
              </Typography>

              {/* Pest rows (view-only) */}
              <Grid container spacing={1.5}>
                {DEGREE_ROWS.map(({ key, label }) => {
                  const selectedLV = (degree?.[key] || '').toUpperCase(); // 'L'|'M'|'H'|''

                  return (
                    <Grid item xs={12} key={key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.25,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {label}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4 }}>
                          {DEGREE_LEVELS.map((lv) => {
                            const isSelected = selectedLV === lv;
                            return (
                              <Button
                                key={lv}
                                disabled
                                sx={ratingBtnSx(isSelected)}
                                aria-label={`${label} ${lv}`}
                              >
                                {lv}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* FINDINGS – Areas list (view-only, prefilled) */}
              <Box component="fieldset" disabled sx={{ mt: 2, border: 0, m: 0, p: 0, pointerEvents: 'none' }}>
                {findingsAreas.map((f, idx) => (
                  <FindingsCard
                    key={f.id}
                    control={control}
                    register={register}
                    index={idx}
                    removeFinding={() => {}}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RECOMMENDATIONS */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  RECOMMENDATIONS
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Add up to {MAX_RECOMMENDATIONS} recommendations
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                {recFields.map((f, idx) => {
                  const selected = recFields?.[idx]?.option ?? f.option ?? "";

                  return (
                    <Grid item xs={12} key={f.id}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Dropdown (view-only) */}
                        <Controller
                          control={control}
                          name={`recommendations.${idx}.option`}
                          defaultValue={f.option ?? ""}
                          render={({ field }) => (
                            <Select
                              {...field}
                              size="small"
                              displayEmpty
                              sx={{ minWidth: 260 }}
                              disabled
                            >
                              {RECOMMENDATION_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value || "none"} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />

                        {/* Free-text when "Other" is selected (view-only) */}
                        {selected === "Other" && (
                          <TextField
                            size="small"
                            label="Specify recommendation"
                            sx={{ flex: '1 1 260px', minWidth: 240 }}
                            {...register(`recommendations.${idx}.other_text`)}
                            disabled
                          />
                        )}

                        {/* Remove row (disabled to keep layout consistent) */}
                        <Box sx={{ ml: 'auto' }}>
                          <IconButton
                            color="error"
                            size="small"
                            disabled
                            aria-label="Remove recommendation"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Add button (disabled for view-only) */}
              <Button
                size="small"
                variant="outlined"
                disabled
                sx={{ mt: 2 }}
              >
                Add Recommendation ({recFields.length}/{MAX_RECOMMENDATIONS})
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* CUSTOMER'S RESPONSIBILITIES Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  CUSTOMER'S RESPONSIBILITIES
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />

            <CardContent sx={{ px: 2, pb: 2 }}>
              {/* SANITATION */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  SANITATION
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Needs improvement (Specify area)
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 1"
                      value={san1}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 2"
                      value={san2}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 3"
                      value={san3}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* RODENT ENTRY POINT */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  RODENT ENTRY POINT
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Needs improvement (Specify area)
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small"
                      label="Sealing"
                      value={sealing}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small"
                      label="Barriers"
                      value={barriers}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Repairs & Maintenance / Renovation */}
              <Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Repairs & Maintenance (Specify area)"
                      value={repairsMaintenance}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Renovation"
                      value={renovation}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chemical Used Section */}
        <Grid item xs={12} md={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" colSpan={2} style={{ fontWeight: "bold" }}>
                          CHEMICAL/S USED
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chemicalsUsedView.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ color: "text.secondary" }}>
                            No chemicals recorded
                          </TableCell>
                        </TableRow>
                      ) : (
                        chemicalsUsedView.map((chemical, index) => (
                          <TableRow key={`${chemical.label ?? index}-${chemical.chemical ?? index}`}>
                            <TableCell>{(chemical.label ?? String(index + 1))}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.chemical ?? ""}
                                disabled
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Against Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  CONTROL AGAINST
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              For Certificate
            </Typography>
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(ca.crawling_insects)}
                      disabled
                    />
                  }
                  label="Crawling Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(ca.flying_insects)}
                      disabled
                    />
                  }
                  label="Flying Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(ca.rodent_control)}
                      disabled
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={Boolean(ca.others)}
                      disabled
                    />
                  }
                  label="Others"
                />
              </FormGroup>

              {Boolean(ca.others) && (
                <TextField
                  label="Please specify"
                  value={ca.others_text ?? ""}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  disabled
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Service Accomplished by Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Service Accomplished by:
              </Typography>
              <Grid container spacing={2}>
                { (jobOrder.accomplished_by_technician || jobOrder.technicians || [])
                    .map((name, i) =>
                      typeof name === "string"
                        ? name
                        : `${name.firstname} ${name.lastname}`
                    )
                    .map((techName, i) => (
                  <Grid key={i} item xs={12}>
                    <TextField
                      label={`Technician ${i + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={techName}
                      disabled
                    />
                  </Grid>
                ))}

                {/* Accomplished Signature */}
                <Grid item xs={12}>
                  {jobOrder.accomplishedSignature && (
                    <Box
                      component="img"
                      src={jobOrder.accomplishedSignature}
                      alt="Accomplished Signature"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        objectFit: "contain",
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                      }}
                    />
                  )}
                </Grid>

                {/* Technician Signatory dropdown, right below the signature */}
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="technician-signatory-label">
                      Technician Signatory
                    </InputLabel>
                    <Select
                      labelId="technician-signatory-label"
                      value={jobOrder.technician_signatory || ""}
                      label="Technician Signatory"
                      disabled
                    >
                      {jobOrder.technicians?.map((tech) => (
                        <MenuItem
                          key={tech.id}
                          value={`${tech.firstname} ${tech.lastname}`}
                        >
                          {`${tech.firstname} ${tech.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder?.technician_signature_uploaded_at && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        🔷 Technician Signature Uploaded At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                        {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      
        {/* Acknowledged Signature Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For Client Only
                </Typography>
              }
              sx={{ px: 2, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Your Satisfaction Rating:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                (1 – lowest, 5 – highest)
              </Typography>
              <Rating
                name="read-only"
                value={Number(jobOrder.satisfaction_rating) || 0}
                readOnly
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Acknowledged By:
              </Typography>
              {jobOrder.acknowledgedSignature && (
                <Box
                  component="img"
                  src={jobOrder.acknowledgedSignature}
                  alt="Acknowledged Signature"
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    objectFit: "contain",
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                  }}
                />
              )}
              <TextField
                label="Client Representative"
                variant="outlined"
                fullWidth
                size="small"
                value={jobOrder.acknowledgedClientSignatory || ""}
                disabled
              />

              {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
              {!isTechnician && jobOrder?.client_signature_uploaded_at && (
                <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50', mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🟢 Client Signature Uploaded At:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {jobOrder?.receipts_url ||
                  jobOrder?.certificate_url ||
                  jobOrder?.reports_url ? (
                  <>
                    {jobOrder?.receipts_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.receipts_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {jobOrder?.certificate_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.certificate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Certificate.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {jobOrder?.reports_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.reports_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Reports.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder?.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Action Buttons Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
          gap={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/manage-service-reports")}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteClick}
            sx={{
              fontSize: "0.875rem",
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
            disabled={isLoading}
          >
            Delete
          </Button>
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="delete-dialog-title">
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography id="delete-dialog-description" sx={{ mb: 2 }}>
              Are you sure you want to delete this service report? This action cannot be undone.
            </Typography>
            
            {jobOrder?.client?.name && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                Client: {jobOrder.client.name}
              </Typography>
            )}
            {jobOrder?.id && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Job Order ID: {jobOrder.id}
              </Typography>
            )}

            <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
              To confirm deletion, please type "DELETE" in the field below:
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Type DELETE to confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              variant="outlined"
              sx={{ mb: 1 }}
              disabled={isLoading}
              autoFocus
            />
            
            {deleteConfirmText && deleteConfirmText.trim().toUpperCase() !== "DELETE" && (
              <Typography variant="caption" color="error">
                Please type "DELETE" exactly as shown
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCancelDelete} 
              color="primary"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              color="error" 
              variant="contained"
              disabled={!isDeleteButtonEnabled}
              sx={{
                opacity: isDeleteButtonEnabled ? 1 : 0.5,
              }}
            >
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </BaseFormWrapper>
  );
}

export default DeleteQcForm;
